import React, { useState } from 'react';
import { Mail, Send, Shield, Info, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast({
        title: "Message Sent Successfully!",
        description: "Thanks! Our team will contact you within one business day.",
      });
      setFormData({ name: '', email: '', phone: '', message: '', website: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-white to-amber-50/20"></div>
        
        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F7931E] mb-4">
            <Mail className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
            Contact Us
          </h1>
          <p className="text-lg text-[#5B6B7F] max-w-2xl mx-auto">
            Get in touch with our team for onboarding, client support, or regulatory compliance inquiries.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="border border-gray-200 shadow-xl rounded-xl bg-white mb-16">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#1E3A5F]">Name *</label>
                    <Input 
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="border border-gray-300 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#1E3A5F]">Email *</label>
                    <Input 
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="border border-gray-300 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[#1E3A5F]">Phone (Optional)</label>
                  <Input 
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="border border-gray-300 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[#1E3A5F]">Message *</label>
                  <Textarea 
                    placeholder="Tell us about your inquiry..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="border border-gray-300 focus:border-[#FF6B35] text-[#1E3A5F] rounded-lg min-h-[150px]"
                    required
                  />
                </div>
                
                {/* Honeypot field - hidden from users */}
                <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                  <Input 
                    type="text"
                    name="website"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#FF6B35] to-[#F7931E] hover:from-[#F7931E] hover:to-[#FF6B35] text-white py-6 text-lg font-semibold rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  {loading ? 'Sending...' : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information Section */}
          <div className="border-t border-gray-200 pt-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#1E3A5F] mb-3 flex items-center justify-center gap-3">
                <Mail className="h-8 w-8 text-[#FF6B35]" />
                Company Contact Directory
              </h2>
              <p className="text-[#5B6B7F] max-w-2xl mx-auto">
                For institutional onboarding, client support or regulatory compliance inquiries, please contact us through our official communication channels.
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-6">
              {/* Primary Contact / General Inquiries */}
              <Card className="border-2 border-gray-200 hover:border-[#FF6B35]/50 transition-all duration-300 rounded-xl">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
                        <Mail className="h-6 w-6 text-[#FF6B35]" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">Primary Contact / General Inquiries</h3>
                      <a href="mailto:info@coinexus.vip" className="text-[#FF6B35] font-medium hover:underline">
                        info@coinexus.vip
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Compliance & AML */}
              <Card className="border-2 border-gray-200 hover:border-[#FF6B35]/50 transition-all duration-300 rounded-xl">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
                        <Shield className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">Compliance & AML</h3>
                      <a href="mailto:compliance@coinexus.vip" className="text-[#FF6B35] font-medium hover:underline">
                        compliance@coinexus.vip
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Client Support */}
              <Card className="border-2 border-gray-200 hover:border-[#FF6B35]/50 transition-all duration-300 rounded-xl">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                        <Info className="h-6 w-6 text-[#1E3A5F]" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">Client Support</h3>
                      <a href="mailto:support@coinexus.vip" className="text-[#FF6B35] font-medium hover:underline">
                        support@coinexus.vip
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Important Notice */}
              <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50/50 to-orange-50/30 rounded-xl">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-amber-900 mb-2">Important</h3>
                      <p className="text-sm text-amber-800 font-medium mb-2">
                        Do not submit any personal documents until instructed by our compliance team.
                      </p>
                      <p className="text-sm text-amber-800">
                        Coinexus follows strict AML/CTF regulations and secure onboarding procedures.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
